import React from 'react';
import {TableInstance} from 'react-table';
import styled from 'styled-components';

import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';

/*
 * Theme Readers.
 */

const findPageColor = (theme: UserTheme) => theme.interactiveText;
const findPageActiveColor = (theme: UserTheme) => theme.greys.white;

const findPageBackgroundColor = (theme: UserTheme) => theme.foreground;
const findPageBackgroundActiveColor = (theme: UserTheme) => theme.palette.blue.shade40;

const findSelectorColor = (theme: UserTheme) => theme.greys.shade70;
const findSelectorBackgroundColor = (theme: UserTheme) => theme.foreground;
const findSelectorHoverColor = (theme: UserTheme) => theme.greys.shade80;
const findSelectorDisabledColor = (theme: UserTheme) => theme.greys.shade50;

const findHoverBackgroundColor = (theme: UserTheme) => theme.alphas.gray10;

const BORDER_RADIUS = 4;

/*
 * Props.
 */

interface TablePageSelectorProps<T extends object> {
  instance: TableInstance<T>;
  isLoading?: boolean;
}

/*
 * Styles.
 */

const StyledPageSelector = styled(ChromelessButton)`
  ${flexCenter};
`;

export const StyledPageSelectorButton = styled(ChromelessButton)<{$disabled: boolean}>`
  margin: 0 3px;
  border-radius: ${BORDER_RADIUS}px;
  color: ${(p) => (p.$disabled ? findSelectorDisabledColor(p.theme) : findSelectorColor(p.theme))};
  svg {
    cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'default')};
  }
  &:hover {
    background-color: ${(p) =>
      p.$disabled ? findSelectorBackgroundColor(p.theme) : findHoverBackgroundColor(p.theme)};
    color: ${(p) => (p.$disabled ? findSelectorDisabledColor(p.theme) : findSelectorHoverColor(p.theme))};
  }
`;

const StyledPageNumber = styled(ChromelessButton)<{$isActive: boolean}>`
  color: ${(p) => (p.$isActive ? findPageActiveColor(p.theme) : findPageColor(p.theme))};
  background-color: ${(p) =>
    p.$isActive ? findPageBackgroundActiveColor(p.theme) : findPageBackgroundColor(p.theme)};
  padding: 3px 7px;
  border-radius: ${BORDER_RADIUS}px;
  font-weight: ${fontWeights.semibold};
  margin: 0 2px;
  &:hover {
    background-color: ${(p) =>
      p.$isActive ? findPageBackgroundActiveColor(p.theme) : findHoverBackgroundColor(p.theme)};
  }
`;

/*
 * Component.
 */

// Based on the current page and the number of total pages, will return the clickable page numbers centered around the current page.
// Some examples:
// currentPage 1 => 1 2 3 4 5
// currentPage 3 => 1 2 3 4 5
// currentPage 5 => 3 4 5 6 7
// currentPage 4 and 4 total pages => 1 2 3 4
// currentPage 5 and 6 total pages => 2 3 4 5 6
export const renderPageNumbers = (
  currentPage: number,
  totalPages: number,
  onClick: (arg: number) => void,
  nbElements = 5,
) => {
  let offsetRight = false;
  let offsetLeft = false;
  let nbPagesAfter = 1;
  let nbPagesBefore = 1;
  const pages = [currentPage];
  while ((!offsetRight || !offsetLeft) && pages.length < nbElements) {
    // Basically we add numbers before and after the current page until we hit 0 on the left, the number of total pages on the right, or `nbElements` pages in total.
    if (currentPage + nbPagesAfter > totalPages) {
      offsetRight = true;
    } else {
      pages.push(currentPage + nbPagesAfter);
      nbPagesAfter++;
    }
    if (currentPage - nbPagesBefore <= 0) {
      offsetLeft = true;
    } else {
      pages.unshift(currentPage - nbPagesBefore);
      nbPagesBefore++;
    }
  }
  return pages.map((page) => renderPageNumber(page, page === currentPage, onClick));
};

const renderPageNumber = (page: number, isActive: boolean, onClick: (arg: number) => void) => (
  <StyledPageNumber key={page} onClick={() => onClick(page - 1)} $isActive={isActive}>
    {page}
  </StyledPageNumber>
);

export const TablePageSelector = <T extends object>({instance, isLoading}: TablePageSelectorProps<T>) => {
  const {
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: {pageIndex},
  } = instance;

  return (
    <StyledPageSelector>
      <StyledPageSelectorButton
        onClick={() => !isLoading && gotoPage(0)}
        $disabled={!canPreviousPage || Boolean(isLoading)}
      >
        <Icon name="firstPageSmall" />
      </StyledPageSelectorButton>
      <StyledPageSelectorButton
        onClick={() => !isLoading && previousPage()}
        $disabled={!canPreviousPage || Boolean(isLoading)}
      >
        <Icon name="arrowBack" />
      </StyledPageSelectorButton>
      {renderPageNumbers(pageIndex + 1, pageCount, gotoPage)}
      <StyledPageSelectorButton
        onClick={() => !isLoading && nextPage()}
        $disabled={!canNextPage || Boolean(isLoading)}
      >
        <Icon name="arrowNext" />
      </StyledPageSelectorButton>
      <StyledPageSelectorButton
        onClick={() => !isLoading && gotoPage(pageCount - 1)}
        $disabled={!canNextPage || Boolean(isLoading)}
      >
        <Icon name="lastPageSmall" />
      </StyledPageSelectorButton>
    </StyledPageSelector>
  );
};
